import React, { useMemo } from "react";
import moment from "moment";
import {
   Chart as ChartJS,
   CategoryScale,
   LinearScale,
   BarElement,
   ArcElement,
   Tooltip,
   Legend,
} from "chart.js";
import { Doughnut, Bar } from "react-chartjs-2";

ChartJS.register(
   CategoryScale,
   LinearScale,
   BarElement,
   ArcElement,
   Tooltip,
   Legend
);

const FLEET_DISTRIBUTION = [
   { label: "Tankers", value: 60, percent: 47, color: "#3b82f6" },
   { label: "Trailers", value: 35, percent: 27, color: "#22c55e" },
   { label: "Trucks", value: 20, percent: 16, color: "#f97316" },
   { label: "Others", value: 13, percent: 10, color: "#8b5cf6" },
];

const FLEET_TOTAL = FLEET_DISTRIBUTION.reduce((sum, item) => sum + item.value, 0);

const TOP_LOCATIONS = [
   { label: "Haldia", value: 22, color: "#3b82f6" },
   { label: "Tapukara", value: 18, color: "#22c55e" },
   { label: "Pune", value: 15, color: "#f97316" },
   { label: "Bankura", value: 12, color: "#8b5cf6" },
   { label: "Manesar", value: 10, color: "#14b8a6" },
];

const RECENT_ACTIVITIES = [
   {
      id: 1,
      text: "Trip WB78B2640 completed",
      time: "2 mins ago",
      tone: "green",
      icon: "fas fa-truck",
   },
   {
      id: 2,
      text: "New vehicle NL01AK1561 added",
      time: "15 mins ago",
      tone: "blue",
      icon: "fas fa-truck",
   },
   {
      id: 3,
      text: "Driver Ajay Jain assigned",
      time: "1 hour ago",
      tone: "sky",
      icon: "fas fa-users",
   },
   {
      id: 4,
      text: "Permit expired for WB98Z1234",
      time: "2 hours ago",
      tone: "red",
      icon: "fas fa-exclamation-triangle",
   },
];

const barValueLabelsPlugin = {
   id: "barValueLabels",
   afterDatasetsDraw(chart) {
      const { ctx } = chart;
      const meta = chart.getDatasetMeta(0);
      if (!meta?.data?.length) return;

      ctx.save();
      ctx.font = "600 12px sans-serif";
      ctx.fillStyle = "#0f2744";
      ctx.textAlign = "center";
      ctx.textBaseline = "bottom";

      meta.data.forEach((bar, index) => {
         const value = chart.data.datasets[0]?.data?.[index];
         if (value == null) return;
         ctx.fillText(String(value), bar.x, bar.y - 6);
      });
      ctx.restore();
   },
};

const doughnutOptions = {
   responsive: true,
   maintainAspectRatio: false,
   cutout: "72%",
   plugins: {
      legend: { display: false },
      tooltip: {
         backgroundColor: "#0f2744",
         padding: 10,
         cornerRadius: 8,
      },
   },
};

const barOptions = {
   responsive: true,
   maintainAspectRatio: false,
   layout: {
      padding: { top: 18 },
   },
   plugins: {
      legend: { display: false },
      tooltip: {
         backgroundColor: "#0f2744",
         padding: 10,
         cornerRadius: 8,
      },
   },
   scales: {
      x: {
         grid: { display: false },
         ticks: { color: "#7b8ba1", font: { size: 11 } },
         border: { display: false },
      },
      y: {
         min: 0,
         max: 25,
         ticks: {
            stepSize: 5,
            color: "#94a3b8",
            font: { size: 11 },
         },
         title: {
            display: true,
            text: "Trips",
            color: "#94a3b8",
            font: { size: 11, weight: "500" },
            align: "end",
         },
         grid: {
            color: "#eef2f7",
            drawBorder: false,
         },
         border: { display: false },
      },
   },
};

const DashboardAnalyticsPanels = ({ fleetDistribution, topLocations, emiData = [] }) => {
   const dynamicFleetDistribution = useMemo(() => {
      if (fleetDistribution?.distribution) {
         const colors = ["#3b82f6", "#22c55e", "#f97316", "#8b5cf6", "#14b8a6"];
         return fleetDistribution.distribution.map((item, index) => ({
            label: item.label,
            value: item.count,
            percent: item.percentage,
            color: colors[index % colors.length]
         }));
      }
      return FLEET_DISTRIBUTION;
   }, [fleetDistribution]);

   const dynamicFleetTotal = fleetDistribution?.total_vehicles ?? FLEET_TOTAL;

   const dynamicTopLocations = useMemo(() => {
      if (topLocations?.locations) {
         const colors = ["#3b82f6", "#22c55e", "#f97316", "#8b5cf6", "#14b8a6"];
         return topLocations.locations.map((item, index) => ({
            label: item.name,
            value: item.fleet_count,
            color: colors[index % colors.length]
         }));
      }
      return TOP_LOCATIONS;
   }, [topLocations]);

   const fleetData = useMemo(
      () => ({
         labels: dynamicFleetDistribution.map((item) => item.label),
         datasets: [
            {
               data: dynamicFleetDistribution.map((item) => item.value),
               backgroundColor: dynamicFleetDistribution.map((item) => item.color),
               borderWidth: 0,
               hoverOffset: 4,
            },
         ],
      }),
      [dynamicFleetDistribution]
   );

   const locationData = useMemo(
      () => ({
         labels: dynamicTopLocations.map((item) => item.label),
         datasets: [
            {
               data: dynamicTopLocations.map((item) => item.value),
               backgroundColor: dynamicTopLocations.map((item) => item.color),
               borderRadius: 8,
               borderSkipped: false,
               barPercentage: 0.55,
               categoryPercentage: 0.7,
            },
         ],
      }),
      [dynamicTopLocations]
   );

   const activeEmiCount = emiData.length;
   const totalEmiObligation = emiData.reduce((sum, item) => {
      const amt = parseFloat(item?.emi_amount) || 0;
      return sum + amt;
   }, 0);

   const formatCurrency = (value) => {
      return new Intl.NumberFormat("en-IN", {
         style: "currency",
         currency: "INR",
         maximumFractionDigits: 0,
      }).format(value);
   };

   return (
      <div className="row oil-analytics-row">
         <div className="col-xl-4 col-lg-6 col-12">
            <div className="oil-panel oil-analytics-panel" style={{ height: "100%", display: "flex", flexDirection: "column" }}>
               <div className="oil-panel__header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <h3 className="oil-panel__title">EMI Perspective</h3>
                  <span className="badge badge-info" style={{ fontSize: "12px", padding: "5px 10px", borderRadius: "12px" }}>
                     {activeEmiCount > 0 ? activeEmiCount : 4} Active Loans
                  </span>
               </div>
               <div className="oil-emi-body" style={{ padding: "15px 5px", display: "flex", flexDirection: "column", gap: "15px", flex: 1 }}>
                  
                  {/* Total Monthly Liability Card */}
                  <div className="oil-emi-card" style={{
                     background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
                     padding: "16px",
                     borderRadius: "12px",
                     color: "#ffffff",
                     display: "flex",
                     flexDirection: "column",
                     gap: "8px",
                     boxShadow: "0 4px 15px rgba(59, 130, 246, 0.2)"
                  }}>
                     <div style={{ fontSize: "12px", opacity: 0.85, fontWeight: "500", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                        Total Monthly Obligation
                     </div>
                     <div style={{ fontSize: "28px", fontWeight: "700", letterSpacing: "-0.5px" }}>
                        {formatCurrency(totalEmiObligation || 156500)} 
                        <span style={{ fontSize: "14px", fontWeight: "400", opacity: 0.9, marginLeft: "4px" }}>/ month</span>
                     </div>
                     <div style={{ fontSize: "11px", opacity: 0.75, display: "flex", alignItems: "center", gap: "4px" }}>
                        <i className="fas fa-info-circle"></i> Calculated from registered vehicle EMIs
                     </div>
                  </div>

                  {/* Upcoming EMIs Section */}
                  <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
                     <div style={{ fontSize: "13px", fontWeight: "600", color: "#475569", marginBottom: "10px", display: "flex", justifyContent: "space-between" }}>
                        <span>Upcoming EMIs This Month</span>
                        <span style={{ fontWeight: "400", color: "#94a3b8" }}>{activeEmiCount > 0 ? activeEmiCount : 4} vehicles</span>
                     </div>
                     
                     <div className="oil-emi-list" style={{ display: "flex", flexDirection: "column", gap: "10px", overflowY: "auto", flex: 1, maxHeight: "205px", paddingRight: "4px" }}>
                        {(emiData.length > 0 ? emiData : [
                           { vehicle_no: "WB78B2640", emi_amount: 35000, financed_by: "HDFC Bank", emi_monthly_obligation_date: 5 },
                           { vehicle_no: "NL01AK1561", emi_amount: 42000, financed_by: "Tata Capital", emi_monthly_obligation_date: 10 },
                           { vehicle_no: "WB98Z1234", emi_amount: 28500, financed_by: "ICICI Bank", emi_monthly_obligation_date: 15 },
                           { vehicle_no: "OD02BA7711", emi_amount: 51000, financed_by: "Cholamandalam", emi_monthly_obligation_date: 20 },
                        ]).map((emi, index) => {
                           const today = moment().startOf("day");
                           const dueDate = emi.emi_monthly_obligation_date
                              ? moment().date(emi.emi_monthly_obligation_date).startOf("day")
                              : null;
                           const diff = dueDate ? dueDate.diff(today, "days") : null;
                           const isToday = diff === 0;
                           const isPassed = diff !== null && diff < 0;
                           const isUpcoming = diff !== null && diff > 0;

                           const statusLabel = isToday ? "Due Today" : isPassed ? "Passed" : isUpcoming ? "Upcoming" : "—";
                           const statusColor = isToday ? "#f59e0b" : isPassed ? "#ef4444" : "#22c55e";
                           const statusBg = isToday ? "#fef3c7" : isPassed ? "#fee2e2" : "#dcfce7";
                           const borderColor = isToday ? "#f59e0b" : isPassed ? "#ef4444" : "#22c55e";

                           return (
                              <div key={index} style={{
                                 display: "flex",
                                 justifyContent: "space-between",
                                 alignItems: "center",
                                 padding: "10px 12px",
                                 background: "#f8fafc",
                                 borderRadius: "8px",
                                 borderLeft: `4px solid ${borderColor}`,
                                 boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
                              }}>
                                 <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                                    <span style={{ fontWeight: "600", fontSize: "13px", color: "#1e293b" }}>{emi.vehicle_no}</span>
                                    <span style={{ fontSize: "11px", color: "#64748b" }}>{emi.financed_by || "Financer"}</span>
                                 </div>
                                 <div style={{ textAlign: "right", display: "flex", flexDirection: "column", gap: "4px", alignItems: "flex-end" }}>
                                    <span style={{ fontWeight: "700", fontSize: "13px", color: "#0f172a" }}>
                                       {formatCurrency(parseFloat(emi.emi_amount) || 0)}
                                    </span>
                                    <span style={{ fontSize: "10px", color: "#64748b" }}>
                                       Due: {dueDate ? dueDate.format("DD-MM-YYYY") : "—"}
                                    </span>
                                    <span style={{
                                       fontSize: "10px",
                                       fontWeight: "600",
                                       color: statusColor,
                                       background: statusBg,
                                       padding: "1px 7px",
                                       borderRadius: "20px",
                                       border: `1px solid ${statusColor}`,
                                       letterSpacing: "0.3px",
                                    }}>
                                       {statusLabel}
                                    </span>
                                 </div>
                              </div>
                           );
                        })}
                     </div>
                  </div>

               </div>
            </div>
         </div>

         <div className="col-xl-8 col-lg-6 col-12">
            <div className="oil-panel oil-analytics-panel">
               <div className="oil-panel__header">
                  <h3 className="oil-panel__title">Top Locations</h3>
               </div>
               <div className="oil-locations-chart">
                  <Bar
                     options={barOptions}
                     data={locationData}
                     plugins={[barValueLabelsPlugin]}
                  />
               </div>
            </div>
         </div>

         {/* <div className="col-xl-4 col-lg-12 col-12">
            <div className="oil-panel oil-analytics-panel">
               <div className="oil-panel__header">
                  <h3 className="oil-panel__title">Recent Activities</h3>
               </div>
               <ul className="oil-activity-list">
                  {RECENT_ACTIVITIES.map((item) => (
                     <li key={item.id} className="oil-activity-item">
                        <span
                           className={`oil-activity-item__icon oil-activity-item__icon--${item.tone}`}
                        >
                           <i className={item.icon} />
                        </span>
                        <span className="oil-activity-item__text">{item.text}</span>
                        <span className="oil-activity-item__time">{item.time}</span>
                     </li>
                  ))}
               </ul>
            </div>
         </div> */}
      </div>
   );
};

export default DashboardAnalyticsPanels;
